/**
 * conversationExport — the whole session, as readable markdown.
 *
 * Built for the clipboard: a lawyer pasting a research session into a memo,
 * an email, or a matter note. So it is the CONVERSATION, in order, followed by
 * a compact register of what was cited.
 *
 * THE SOURCES SECTION IS A REFERENCE LIST, NOT AN ARCHIVE. Each entry is a
 * name, a link, and one short line about the source. Full source content is
 * NEVER inlined: the documents are long, frequently licensed, and pasting a
 * corpus law into an email is not what anyone asked for.
 *
 * WHAT THAT LINE IS, AND WHAT IT IS NOT (BUG-01). It used to be
 * `supports — justification`, both of which are text the MODEL wrote, formatted
 * exactly like a pinpoint quotation — and this header used to state that
 * `snippet` was "deliberately not used". So a lawyer pasting a session into a
 * memo pasted a list of authorities each annotated with a sentence from the
 * answer, indistinguishable from a quotation of the authority itself.
 *
 * The register now leads with `quote`, the VERBATIM span of the document, and
 * falls back to labelling the model's assertion as "Cited for: …" when there is
 * no quote. Reference rather than reproduce still holds: the quote is one
 * clipped passage, not the source.
 */

import { ResearchCitation } from './types';
import { ResearchTurn, isAssistantTurn } from './turns';
import { buildDocument } from './documentModel';
import { documentToText } from './artifactExport';

/** How much of a summary line survives — a register entry, not a paragraph. */
export const SUMMARY_LIMIT = 240;

function clamp(text: string, limit = SUMMARY_LIMIT): string {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).replace(/\s+\S*$/, '')}…`;
}

/**
 * Where the reader can find this source.
 *
 * A public url when there is one. Otherwise the fact that it is stored rather
 * than linkable, which is more honest than an empty column — and an uploaded
 * document is called out as the user's own file.
 */
export function citationLink(citation: ResearchCitation): string {
  const url = String(citation.url || '').trim();
  if (url) return url;
  if (
    String((citation as any).source_type || '').toLowerCase() === 'uploaded_document' ||
    citation.provider === 'uploaded_document'
  ) {
    const name = String(citation.file_name || '').trim();
    return name ? `[uploaded: ${name}]` : '[uploaded document]';
  }
  const blobPath = String(citation.blob_path || '').trim();
  return blobPath ? `[stored: ${blobPath}]` : '[no link]';
}

/**
 * The one-line register entry for a source.
 *
 * A real quotation when the engine located one, in quotation marks so it reads
 * as the document's words. Otherwise the model's own line, explicitly prefixed
 * "Cited for:" — never bare, because bare is what made it look quoted.
 */
export function citationSummary(citation: ResearchCitation): string {
  const quote = clamp(citation.quote || '');
  if (quote) return `“${quote}”`;

  const asserts = clamp(citation.asserts || citation.supports || '');
  const justification = clamp(citation.justification || '');
  const claim = asserts && justification
    ? clamp(`${asserts} — ${justification}`)
    : asserts || justification;
  return claim ? `Cited for: ${claim}` : '';
}

/** Every citation in the chat, in first-cited order, one entry per document. */
export function conversationCitations(turns: ResearchTurn[]): ResearchCitation[] {
  const seen = new Set<string>();
  const citations: ResearchCitation[] = [];

  turns.forEach((turn) => {
    if (!isAssistantTurn(turn)) return;
    (turn.response?.citations || []).forEach((citation) => {
      // The same authority cited by three turns is one source, but two
      // different documents can share a title, so the identity is the
      // document — not the display number, which restarts every turn.
      const key = (
        citation.blob_path || citation.document_uuid || citation.url || citation.title || ''
      ).trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      citations.push(citation);
    });
  });

  return citations;
}

/**
 * A prose answer, serialised.
 *
 * `buildDocument` covers the document-shaped intents (memo, draft,
 * chronology); a `qa` / `overview` / `bullets` payload renders straight from
 * its fields on screen and would otherwise export as nothing at all. What is
 * copied has to be what the reader saw.
 */
function proseText(payload: Record<string, any>): string {
  const lines: string[] = [];
  const lead = String(payload.answer || payload.prose || '').trim();
  if (lead) lines.push(lead, '');

  const points = Array.isArray(payload.key_points) ? payload.key_points : [];
  points.forEach((point: any) => {
    const text = typeof point === 'string' ? point : String(point?.text || point?.point || '');
    if (text.trim()) lines.push(`- ${text.trim()}`);
  });
  if (points.length) lines.push('');

  const bullets = Array.isArray(payload.bullets) ? payload.bullets : [];
  bullets.forEach((bullet: any) => {
    const text = typeof bullet === 'string' ? bullet : String(bullet?.text || '');
    if (text.trim()) lines.push(`- ${text.trim()}`);
  });
  if (bullets.length) lines.push('');

  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  sections.forEach((section: any) => {
    const heading = String(section?.heading || '').trim();
    const body = String(section?.body || '').trim();
    if (heading) lines.push(`### ${heading}`, '');
    if (body) lines.push(body, '');
  });

  const caveats = Array.isArray(payload.caveats) ? payload.caveats : [];
  caveats.forEach((caveat: any) => {
    const text = String(caveat || '').trim();
    if (text) lines.push(`> ${text}`);
  });

  return lines.join('\n').trim();
}

/** The answer text for one assistant turn, however it is stored. */
function answerText(turn: ResearchTurn): string {
  if (!isAssistantTurn(turn)) return '';
  if (turn.response) {
    const text = documentToText(buildDocument(turn.response, ''));
    if (text.trim()) return text.trim();

    const prose = proseText((turn.response.payload || {}) as Record<string, any>);
    if (prose) return prose;

    if (turn.response.markdown.trim()) return turn.response.markdown.trim();
  }
  // A turn whose blob has not been hydrated still has its stored summary,
  // which is a real answer and better than a hole in the transcript.
  return String(turn.summary || '').trim();
}

export interface ConversationExportOptions {
  title?: string;
  /** Rendered under the title — the chat's own timestamp, already formatted. */
  subtitle?: string;
}

/**
 * The whole session as markdown.
 *
 * Turns render in the order given; the caller orders them (`orderTurns`) so
 * this stays a pure serializer.
 */
export function conversationToMarkdown(
  turns: ResearchTurn[],
  options: ConversationExportOptions = {}
): string {
  const lines: string[] = [];
  const title = (options.title || '').trim();

  lines.push(`# ${title || 'Research conversation'}`);
  if (options.subtitle?.trim()) lines.push('', `_${options.subtitle.trim()}_`);
  lines.push('');

  let exchange = 0;
  turns.forEach((turn) => {
    if (turn.kind === 'user') {
      exchange += 1;
      const question = turn.text.trim();
      if (question) {
        lines.push(`## ${exchange}. ${question}`, '');
      } else {
        // A message can be an attachment with no words.
        lines.push(`## ${exchange}.`, '');
      }
      // Files are named, never inlined.
      (turn.attachments || []).forEach((attachment) => {
        lines.push(`_Attached: ${attachment.name}_`);
      });
      if (turn.attachments?.length) lines.push('');
      return;
    }

    const answer = answerText(turn);
    if (answer) {
      lines.push(answer, '');
    } else if (turn.live?.error) {
      lines.push(`_${turn.live.error}_`, '');
    }
  });

  const citations = conversationCitations(turns);
  if (citations.length > 0) {
    lines.push('---', '', '## Sources', '');
    citations.forEach((citation, i) => {
      const heading = (
        citation.title || citation.document_number || citation.file_name || `Source ${i + 1}`
      ).trim();
      lines.push(`${i + 1}. **${heading}**`);

      const meta = [citation.document_type, citation.authority, citation.date]
        .map((v) => String(v || '').trim())
        .filter(Boolean);
      if (meta.length) lines.push(`   ${meta.join(' · ')}`);

      lines.push(`   ${citationLink(citation)}`);

      const summary = citationSummary(citation);
      if (summary) lines.push(`   ${summary}`);
      lines.push('');
    });
  }

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Copy the conversation.
 *
 * Resolves false when there is no clipboard (an insecure origin, an old
 * browser) so the caller can say so rather than silently doing nothing.
 */
export async function copyConversation(
  turns: ResearchTurn[],
  options: ConversationExportOptions = {}
): Promise<boolean> {
  const text = conversationToMarkdown(turns, options);
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
